import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import ProjectImageUpload from '../components/ProjectImageUpload';
import { getProjectImageUrl, UPLOADS_URL } from '../config';
import { toast } from '../components/Toast';
import {
  ShoppingCart, Monitor, Settings, Wheat, Home, Truck, Utensils, Shirt,
  HeartPulse, BookOpen, Coins, Folder, Handshake, Briefcase, GraduationCap,
  Link as LinkIcon, Ban, ClipboardList, CheckCircle2, XCircle, Clock,
  Wallet as WalletIcon, Calendar, FileText, TrendingUp, Trash2, Eye, Edit,
  User, Phone, Lightbulb, Inbox, FolderOpen
} from 'lucide-react';

const CATEGORY_ICONS = {
  commerce: ShoppingCart, tech: Monitor, services: Settings, agriculture: Wheat,
  immobilier: Home, transport: Truck, restauration: Utensils, mode: Shirt,
  sante: HeartPulse, education: BookOpen, finance: Coins, autre: Folder
};

const TASK_TYPE_ICONS = {
  collaboration: Handshake, prestation: Coins, emploi: Briefcase,
  stage: GraduationCap, partenariat: LinkIcon
};

const AuthorDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  const [stats, setStats] = useState({ projects: 0, tasks: 0, applications: 0, pending: 0, suspended: 0 });
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: string }
  const [confirmModal, setConfirmModal] = useState(null); // { type, id, title, message, confirmText, onConfirm }
  
  // Filtres pour Projets
  const [projectSearch, setProjectSearch] = useState('');
  const [projectStatus, setProjectStatus] = useState('all');
  const [projectCategory, setProjectCategory] = useState('all');
  const [projectSort, setProjectSort] = useState('recent');
  const [projectPage, setProjectPage] = useState(1);
  
  // Filtres pour Postes
  const [taskSearch, setTaskSearch] = useState('');
  const [taskType, setTaskType] = useState('all');
  const [taskSort, setTaskSort] = useState('recent');
  const [taskPage, setTaskPage] = useState(1);
  
  // Filtres pour Candidatures
  const [appSearch, setAppSearch] = useState('');
  const [appStatus, setAppStatus] = useState('all');
  const [appSort, setAppSort] = useState('recent');
  const [appPage, setAppPage] = useState(1);
  
  const itemsPerPage = 6;

  const { user } = useContext(AuthContext);
  const location = useLocation();

  // Catégories business
  const categories = [
    { value: 'commerce', label: 'Commerce', Icon: ShoppingCart, color: 'bg-blue-500' },
    { value: 'tech', label: 'Tech & Digital', Icon: Monitor, color: 'bg-purple-500' },
    { value: 'services', label: 'Services', Icon: Settings, color: 'bg-green-500' },
    { value: 'agriculture', label: 'Agriculture', Icon: Wheat, color: 'bg-yellow-500' },
    { value: 'immobilier', label: 'Immobilier', Icon: Home, color: 'bg-red-500' },
    { value: 'transport', label: 'Transport', Icon: Truck, color: 'bg-indigo-500' },
    { value: 'restauration', label: 'Restauration', Icon: Utensils, color: 'bg-orange-500' },
    { value: 'mode', label: 'Mode & Beauté', Icon: Shirt, color: 'bg-pink-500' },
    { value: 'sante', label: 'Santé', Icon: HeartPulse, color: 'bg-teal-500' },
    { value: 'education', label: 'Éducation', Icon: BookOpen, color: 'bg-cyan-500' },
    { value: 'finance', label: 'Finance', Icon: Coins, color: 'bg-emerald-500' },
    { value: 'autre', label: 'Autre', Icon: Folder, color: 'bg-gray-500' }
  ];

  // Types de postes business
  const taskTypes = [
    { value: 'collaboration', label: 'Associé', Icon: Handshake, color: 'bg-purple-100 text-purple-700 border-purple-300' },
    { value: 'prestation', label: 'Freelance', Icon: Coins, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { value: 'emploi', label: 'Emploi', Icon: Briefcase, color: 'bg-green-100 text-green-700 border-green-300' },
    { value: 'stage', label: 'Stage', Icon: GraduationCap, color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    { value: 'partenariat', label: 'Partenariat', Icon: LinkIcon, color: 'bg-orange-100 text-orange-700 border-orange-300' }
  ];

  const getCategoryInfo = (cat) => categories.find(c => c.value === cat) || categories[categories.length - 1];
  const getTaskTypeInfo = (type) => taskTypes.find(t => t.value === type) || taskTypes[0];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsRes, tasksRes, appsRes] = await Promise.all([
        api.get('/projects/my').catch(() => ({ data: [] })),
        api.get('/tasks/my').catch(() => ({ data: [] })),
        api.get('/applications/received').catch(() => ({ data: [] }))
      ]);
      
      const projectsData = Array.isArray(projectsRes.data) ? projectsRes.data : [];
      const tasksData = Array.isArray(tasksRes.data) ? tasksRes.data : [];
      const appsData = Array.isArray(appsRes.data) ? appsRes.data : [];
      
      // Charger les images pour chaque projet
      const projectsWithImages = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const imagesRes = await api.get(`/projects/${project.id}/images`);
            return { ...project, images: imagesRes.data || [] };
          } catch {
            return { ...project, images: [] };
          }
        })
      );
      
      setProjects(projectsWithImages);
      setTasks(tasksData);
      setApplications(appsData);
      
      setStats({
        projects: projectsData.length,
        tasks: tasksData.length,
        applications: appsData.length,
        pending: appsData.filter(a => a.statut === 'en_attente').length,
        suspended: projectsData.filter(p => p.is_suspended).length,
        active: projectsData.filter(p => p.statut === 'en_cours').length
      });
    } catch (err) {
      console.error(err);
      setProjects([]);
      setTasks([]);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [location.key, loadData]);

  // Reset pages quand les filtres changent
  useEffect(() => { setProjectPage(1); }, [projectSearch, projectStatus, projectCategory, projectSort]);
  useEffect(() => { setTaskPage(1); }, [taskSearch, taskType, taskSort]);
  useEffect(() => { setAppPage(1); }, [appSearch, appStatus, appSort]);

  // Filtrage et tri des Projets
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];
    
    if (projectSearch) {
      const search = projectSearch.toLowerCase();
      filtered = filtered.filter(p => 
        p.titre?.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search) ||
        p.lieu_ville?.toLowerCase().includes(search)
      );
    }
    
    if (projectStatus !== 'all') {
      filtered = filtered.filter(p => p.statut === projectStatus);
    }
    
    if (projectCategory !== 'all') {
      filtered = filtered.filter(p => p.categorie === projectCategory);
    }
    
    filtered.sort((a, b) => {
      if (projectSort === 'recent') return new Date(b.created_at) - new Date(a.created_at);
      if (projectSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (projectSort === 'alpha') return (a.titre || '').localeCompare(b.titre || '');
      if (projectSort === 'budget') return (parseFloat(b.budget) || 0) - (parseFloat(a.budget) || 0);
      return 0;
    });
    
    return filtered;
  }, [projects, projectSearch, projectStatus, projectCategory, projectSort]);

  // Filtrage et tri des Tâches
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];
    
    if (taskSearch) {
      const search = taskSearch.toLowerCase();
      filtered = filtered.filter(t => 
        t.titre?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search) ||
        t.project_titre?.toLowerCase().includes(search)
      );
    }
    
    if (taskType !== 'all') {
      filtered = filtered.filter(t => t.type === taskType);
    }
    
    filtered.sort((a, b) => {
      if (taskSort === 'recent') return new Date(b.created_at) - new Date(a.created_at);
      if (taskSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (taskSort === 'remuneration') return (parseFloat(b.remuneration) || 0) - (parseFloat(a.remuneration) || 0);
      return 0;
    });
    
    return filtered;
  }, [tasks, taskSearch, taskType, taskSort]);

  // Filtrage et tri des Candidatures
  const filteredApps = useMemo(() => {
    let filtered = [...applications];
    
    if (appSearch) {
      const search = appSearch.toLowerCase();
      filtered = filtered.filter(a => 
        a.nom?.toLowerCase().includes(search) ||
        a.user_nom?.toLowerCase().includes(search) ||
        a.task_titre?.toLowerCase().includes(search) ||
        a.project_titre?.toLowerCase().includes(search)
      );
    }
    
    if (appStatus !== 'all') {
      filtered = filtered.filter(a => a.statut === appStatus);
    }
    
    filtered.sort((a, b) => {
      const dateA = new Date(a.date_candidature || a.created_at);
      const dateB = new Date(b.date_candidature || b.created_at);
      if (appSort === 'recent') return dateB - dateA;
      if (appSort === 'oldest') return dateA - dateB;
      if (appSort === 'status') {
        const order = { 'en_attente': 0, 'acceptee': 1, 'refusee': 2 };
        return (order[a.statut] || 3) - (order[b.statut] || 3);
      }
      return 0;
    });
    
    return filtered;
  }, [applications, appSearch, appStatus, appSort]);

  // Pagination
  const paginatedProjects = filteredProjects.slice((projectPage - 1) * itemsPerPage, projectPage * itemsPerPage);
  const paginatedTasks = filteredTasks.slice((taskPage - 1) * itemsPerPage, taskPage * itemsPerPage);
  const paginatedApps = filteredApps.slice((appPage - 1) * itemsPerPage, appPage * itemsPerPage);
  
  const totalProjectPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const totalTaskPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const totalAppPages = Math.ceil(filteredApps.length / itemsPerPage);

  const handleDeleteProject = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setConfirmModal({
      type: 'danger',
      title: 'Supprimer ce projet ?',
      message: `Vous êtes sur le point de supprimer "${project?.title || 'ce projet'}". Cette action est irréversible et supprimera également tous les postes et candidatures associés.`,
      confirmText: 'Oui, supprimer',
      onConfirm: async () => {
        try {
          await api.delete(`/projects/${projectId}`);
          setProjects(projects.filter(p => p.id !== projectId));
          loadData();
          setNotification({ type: 'success', message: 'Projet supprimé avec succès' });
          setTimeout(() => setNotification(null), 5000);
        } catch (err) {
          setNotification({ type: 'error', message: 'Erreur lors de la suppression du projet' });
          setTimeout(() => setNotification(null), 5000);
        }
        setConfirmModal(null);
      }
    });
  };

  const handleApplicationAction = async (appId, action) => {
    try {
      await api.put(`/applications/${appId}`, { statut: action });
      setApplications(applications.map(a => 
        a.id === appId ? { ...a, statut: action } : a
      ));
    } catch (err) {
      toast.error('Erreur lors de l\'action');
    }
  };

  const handleDeleteTask = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    setConfirmModal({
      type: 'danger',
      title: 'Supprimer ce poste ?',
      message: `Vous êtes sur le point de supprimer le poste "${task?.titre || 'ce poste'}". Cette action est irréversible et supprimera également toutes les candidatures associées.`,
      confirmText: 'Oui, supprimer',
      onConfirm: async () => {
        try {
          await api.delete(`/tasks/${taskId}`);
          setTasks(tasks.filter(t => t.id !== taskId));
          setNotification({ type: 'success', message: 'Poste supprimé avec succès' });
          setTimeout(() => setNotification(null), 5000);
        } catch (err) {
          setNotification({ type: 'error', message: 'Erreur lors de la suppression du poste' });
          setTimeout(() => setNotification(null), 5000);
        }
        setConfirmModal(null);
      }
    });
  };

  const formatBudget = (amount) => {
    if (!amount) return null;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
  };

  // Gestion du feedback d'upload
  const handleUploadFeedback = (feedback) => {
    setNotification({
      type: feedback.success ? 'success' : 'error',
      message: feedback.message
    });
    // Auto-masquer après 5 secondes
    setTimeout(() => setNotification(null), 5000);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Composant Pagination
  const Pagination = ({ currentPage, totalPages, setPage }) => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    
    return (
      <div className="flex justify-center items-center gap-1 mt-8">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        {pages.map((page, idx) => (
          page === '...' ? (
            <span key={idx} className="px-3 py-2 text-gray-400">...</span>
          ) : (
            <button
              key={page}
              onClick={() => setPage(page)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                currentPage === page 
                  ? 'bg-indigo-600 text-white' 
                  : 'border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          )
        ))}
        
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  };

  // Composant Stats Card
  const StatsCard = ({ icon, value, label, color, onClick, badge }) => (
    <div 
      onClick={onClick}
      className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all duration-200 ${color}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span>{icon}</span>
        {badge && (
          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
            {badge}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-gray-800 mb-1">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Chargement de votre espace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Hero */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Welcome Section */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <span className="text-2xl font-bold">{user?.nom?.[0] || 'U'}</span>
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold">Bonjour, {user?.nom || 'Entrepreneur'}</h1>
                  <p className="text-indigo-200">Gérez vos projets et équipes</p>
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <Link
                to="/create-project"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-indigo-50 transition shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nouveau Projet
              </Link>
              <Link
                to="/notifications"
                className="inline-flex items-center gap-2 px-4 py-3 bg-white/20 backdrop-blur text-white font-medium rounded-xl hover:bg-white/30 transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Notifications
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        {/* Toast Notification for Upload Feedback */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 max-w-md p-4 rounded-xl shadow-2xl transform transition-all duration-300 ease-out animate-slide-in ${
            notification.type === 'success' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
              : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-white/20' : 'bg-white/20'
              }`}>
                {notification.type === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-lg">
                  {notification.type === 'success' ? 'Succès !' : 'Erreur'}
                </p>
                <p className="text-sm text-white/90 mt-1">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="text-white/70 hover:text-white transition p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Alert for Suspended Projects */}
        {stats.suspended > 0 && (
          <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-2xl shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Ban size={24} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 text-lg">
                  {stats.suspended} projet{stats.suspended > 1 ? 's' : ''} suspendu{stats.suspended > 1 ? 's' : ''}
                </h3>
                <p className="text-red-600 text-sm mt-1">
                  Un ou plusieurs de vos projets ont été suspendus. Consultez vos notifications pour plus de détails.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link 
                    to="/notifications" 
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition"
                  >
                    Voir les notifications
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notice des obligations de l'auteur */}
        <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <ClipboardList size={24} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-800 text-lg mb-2">
                Rappel de vos obligations en tant qu'auteur
              </h3>
              <ul className="text-amber-700 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span><strong>Transparence :</strong> Gérez vos projets depuis le <strong>Workspace</strong> de chaque projet pour assurer une transparence totale avec vos collaborateurs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span><strong>Paiements :</strong> Réglez les paiements de vos collaborateurs et les commissions de la plateforme <strong>au moins une fois par mois</strong>.</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-700 font-medium flex items-start gap-2">
                  <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <span>
                    Le non-respect de ces obligations peut entraîner la <strong>suspension de vos projets</strong>, 
                    des <strong>poursuites judiciaires</strong>, et la possibilité d'un <strong>audit surprise</strong> par un agent DJAMANN.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatsCard 
            icon={<Folder size={24} />} 
            value={stats.projects} 
            label="Projets" 
            onClick={() => setActiveTab('projects')}
          />
          <StatsCard 
            icon={<CheckCircle2 size={24} className="text-green-500" />} 
            value={stats.active} 
            label="En cours" 
            onClick={() => { setActiveTab('projects'); setProjectStatus('en_cours'); }}
          />
          <StatsCard 
            icon={<Briefcase size={24} />} 
            value={stats.tasks} 
            label="Postes ouverts" 
            onClick={() => setActiveTab('tasks')}
          />
          <StatsCard 
            icon={<FileText size={24} />} 
            value={stats.applications} 
            label="Candidatures" 
            onClick={() => setActiveTab('applications')}
          />
          <StatsCard 
            icon={<Clock size={24} className="text-amber-500" />} 
            value={stats.pending} 
            label="En attente" 
            onClick={() => { setActiveTab('applications'); setAppStatus('en_attente'); }}
            badge={stats.pending > 0 ? stats.pending : null}
          />
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6">
          <div className="flex overflow-x-auto">
            {[
              { id: 'projects', label: 'Mes Projets', icon: <Folder size={18} />, count: filteredProjects.length },
              { id: 'tasks', label: 'Postes ouverts', icon: <Briefcase size={18} />, count: filteredTasks.length },
              { id: 'applications', label: 'Candidatures', icon: <FileText size={18} />, count: filteredApps.length, badge: stats.pending }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[150px] py-4 px-6 text-center font-medium transition-all relative ${
                  activeTab === tab.id 
                    ? 'text-indigo-600 bg-indigo-50' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                  {tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {tab.badge}
                    </span>
                  )}
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Rechercher un projet..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={projectCategory}
                  onChange={(e) => setProjectCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="all">Toutes catégories</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <select
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="all">Tous statuts</option>
                  <option value="en_cours">● En cours</option>
                  <option value="termine">✓ Terminé</option>
                  <option value="annule">✗ Annulé</option>
                </select>
                <select
                  value={projectSort}
                  onChange={(e) => setProjectSort(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="recent">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                  <option value="alpha">A → Z</option>
                  <option value="budget">Budget ↓</option>
                </select>
              </div>
            </div>

            {/* Results Info */}
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                <span className="font-bold text-indigo-600">{filteredProjects.length}</span> projet{filteredProjects.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Projects Grid */}
            {paginatedProjects.length > 0 ? (
              <div className="grid gap-6">
                {paginatedProjects.map(project => {
                  const catInfo = getCategoryInfo(project.categorie);
                  const projectTasks = tasks.filter(t => t.project_id === project.id);
                  
                  return (
                    <div 
                      key={project.id} 
                      className={`bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-200 ${
                        project.is_suspended ? 'border-red-300 bg-red-50/50' : 'border-gray-100'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row">
                        {/* Cover Image / Category Color */}
                        <div className={`lg:w-48 h-32 lg:h-auto ${catInfo.color} relative flex-shrink-0 overflow-hidden`}>
                          {project.image_couverture ? (
                            <img 
                              src={getProjectImageUrl(project.image_couverture)} 
                              alt={project.titre}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : project.images && project.images.length > 0 ? (
                            <img 
                              src={`${UPLOADS_URL}${project.images[0].image_path}`}
                              alt={project.titre}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {catInfo.Icon && <catInfo.Icon size={48} className="opacity-50 text-white" />}
                            </div>
                          )}
                          {/* Status Badge */}
                          <div className="absolute top-3 left-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              project.statut === 'en_cours' ? 'bg-green-500 text-white' :
                              project.statut === 'termine' ? 'bg-gray-500 text-white' :
                              'bg-green-500 text-white'
                            }`}>
                              {project.statut === 'en_cours' ? 'En cours' : 
                               project.statut === 'termine' ? 'Terminé' : ''}
                            </span>
                          </div>
                          {project.is_suspended && (
                            <div className="absolute top-3 right-3">
                              <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-medium animate-pulse">
                                Suspendu
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-6">
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div className="flex-1">
                              {/* Category Badge */}
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full mb-2">
                                {catInfo.Icon && <catInfo.Icon size={14} className="inline" />} {catInfo.label}
                              </span>
                              
                              <h3 className="text-xl font-bold text-gray-800 mb-2">
                                <Link to={`/project/${project.id}`} className="hover:text-indigo-600 transition">
                                  {project.titre}
                                </Link>
                              </h3>
                              
                              {project.is_suspended && project.suspension_reason && (
                                <div className="mb-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                                  <p className="text-red-700 text-sm">
                                    <strong>Motif :</strong> {project.suspension_reason}
                                  </p>
                                </div>
                              )}
                              
                              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                {project.description || 'Aucune description'}
                              </p>

                              {/* Project Meta */}
                              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
                                {project.lieu_ville && (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {project.lieu_ville}{project.lieu_pays ? `, ${project.lieu_pays}` : ''}
                                  </span>
                                )}
                                {project.budget && (
                                  <span className="flex items-center gap-1 text-green-600 font-medium">
                                    <Coins size={14} className="inline" /> {formatBudget(project.budget)}
                                  </span>
                                )}
                                {project.deadline && (
                                  <span className="flex items-center gap-1">
                                    <Calendar size={14} className="inline" /> {formatDate(project.deadline)}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Briefcase size={14} className="inline" /> {projectTasks.length} poste{projectTasks.length !== 1 ? 's' : ''}
                                </span>
                                <span className="flex items-center gap-1">
                                  <FileText size={14} className="inline" /> {applications.filter(a => a.project_id === project.id).length} candidature{applications.filter(a => a.project_id === project.id).length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2 lg:items-end">
                              <ProjectImageUpload 
                                projectId={project.id} 
                                onUpload={loadData} 
                                onFeedback={handleUploadFeedback}
                              />
                              <div className="flex flex-wrap gap-2">
                                <Link
                                  to={`/project/${project.id}`}
                                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
                                >
                                  <Eye size={16} className="inline mr-1" />Ouvrir le projet
                                </Link>
                                <Link
                                  to={`/edit-project/${project.id}`}
                                  className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition text-sm font-medium"
                                >
                                  <Edit size={16} className="inline mr-1" />Modifier
                                </Link>
                                <button
                                  onClick={() => handleDeleteProject(project.id)}
                                  className="px-4 py-2 bg-red-500 text-red-700 rounded-lg hover:bg-red-200 transition text-sm font-medium"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Folder size={40} className="text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {projectSearch || projectStatus !== 'all' || projectCategory !== 'all' 
                    ? 'Aucun projet trouvé' 
                    : 'Aucun projet'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {projectSearch || projectStatus !== 'all' || projectCategory !== 'all'
                    ? 'Essayez de modifier vos filtres'
                    : 'Créez votre premier projet pour commencer'}
                </p>
                {!projectSearch && projectStatus === 'all' && projectCategory === 'all' && (
                  <Link
                    to="/create-project"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Créer mon premier projet
                  </Link>
                )}
              </div>
            )}
            
            <Pagination currentPage={projectPage} totalPages={totalProjectPages} setPage={setProjectPage} />
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Rechercher un poste..."
                    value={taskSearch}
                    onChange={(e) => setTaskSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={taskSort}
                  onChange={(e) => setTaskSort(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="recent">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                  <option value="remuneration">Rémunération ↓</option>
                </select>
              </div>
              
              {/* Type Filters */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setTaskType('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                    taskType === 'all' 
                      ? 'bg-indigo-600 text-white border-indigo-600' 
                      : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  Tous les types
                </button>
                {taskTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setTaskType(type.value)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                      taskType === type.value 
                        ? `${type.color} border-current` 
                        : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    {React.createElement(type.Icon, { size: 16, className: 'inline mr-1' })} {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Info */}
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                <span className="font-bold text-indigo-600">{filteredTasks.length}</span> poste{filteredTasks.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Tasks Grid */}
            {paginatedTasks.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {paginatedTasks.map(task => {
                  const typeInfo = getTaskTypeInfo(task.type);
                  const taskApps = applications.filter(a => a.task_id === task.id);
                  
                  return (
                    <div 
                      key={task.id} 
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition"
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl ${typeInfo.color} flex items-center justify-center text-2xl border`}>
                            {typeInfo.Icon && <typeInfo.Icon size={24} />}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800">{task.titre}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color} border`}>
                              {typeInfo.label}
                            </span>
                          </div>
                        </div>
                        {(task.remuneration || task.valeur_parts > 0) && (
                          <div className="flex gap-2">
                            {task.remuneration && (
                              <span className="px-3 py-1 bg-green-100 text-green-700 font-bold text-sm rounded-full whitespace-nowrap">
                                <Coins size={14} className="inline" /> {formatBudget(task.remuneration)}
                              </span>
                            )}
                            {task.valeur_parts > 0 && (
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 font-bold text-sm rounded-full whitespace-nowrap">
                                <TrendingUp size={14} className="inline" /> {task.valeur_parts}%
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {task.description || 'Aucune description'}
                      </p>
                      
                      {/* Competences */}
                      {task.competences_requises && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {task.competences_requises.split(',').slice(0, 3).map((comp, idx) => (
                            <span key={idx} className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                              {comp.trim()}
                            </span>
                          ))}
                          {task.competences_requises.split(',').length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                              +{task.competences_requises.split(',').length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Meta */}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                        <span className="flex items-center gap-1">
                          <Folder size={12} className="inline" /> {task.project_titre || 'Projet'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText size={12} className="inline" /> {taskApps.length} candidature{taskApps.length !== 1 ? 's' : ''}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={12} className="inline" /> {formatDate(task.created_at)}
                        </span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Link
                          to={`/project/${task.project_id}`}
                          className="flex-1 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition text-sm font-medium text-center"
                        >
                          Voir le projet
                        </Link>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase size={40} className="text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {taskSearch || taskType !== 'all' ? 'Aucun poste trouvé' : 'Aucun poste ouvert'}
                </h3>
                <p className="text-gray-500">
                  {taskSearch || taskType !== 'all'
                    ? 'Essayez de modifier vos filtres'
                    : 'Ajoutez des postes à vos projets pour recruter'}
                </p>
              </div>
            )}
            
            <Pagination currentPage={taskPage} totalPages={totalTaskPages} setPage={setTaskPage} />
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-6">
            {/* Quick Link */}
            <div className="flex justify-end">
              <Link
                to="/received-applications"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm"
              >
                <Inbox size={16} className="inline mr-1" />Voir tous les détails
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            
            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Rechercher par nom, poste, projet..."
                    value={appSearch}
                    onChange={(e) => setAppSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={appSort}
                  onChange={(e) => setAppSort(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="recent">Plus récentes</option>
                  <option value="oldest">Plus anciennes</option>
                  <option value="status">Par statut</option>
                </select>
              </div>
              
              {/* Status Filters */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setAppStatus('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                    appStatus === 'all' 
                      ? 'bg-indigo-600 text-white border-indigo-600' 
                      : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  Toutes ({applications.length})
                </button>
                <button
                  onClick={() => setAppStatus('en_attente')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                    appStatus === 'en_attente' 
                      ? 'bg-yellow-500 text-white border-yellow-500' 
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:border-yellow-400'
                  }`}
                >
                  En attente ({applications.filter(a => a.statut === 'en_attente').length})
                </button>
                <button
                  onClick={() => setAppStatus('acceptee')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                    appStatus === 'acceptee' 
                      ? 'bg-green-500 text-white border-green-500' 
                      : 'bg-green-50 text-green-700 border-green-200 hover:border-green-400'
                  }`}
                >
                  Acceptées ({applications.filter(a => a.statut === 'acceptee').length})
                </button>
                <button
                  onClick={() => setAppStatus('refusee')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition border ${
                    appStatus === 'refusee' 
                      ? 'bg-red-500 text-white border-red-500' 
                      : 'bg-red-50 text-red-700 border-red-200 hover:border-red-400'
                  }`}
                >
                  Refusées ({applications.filter(a => a.statut === 'refusee').length})
                </button>
              </div>
            </div>

            {/* Results Info */}
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                <span className="font-bold text-indigo-600">{filteredApps.length}</span> candidature{filteredApps.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            {/* Applications List */}
            {paginatedApps.length > 0 ? (
              <div className="space-y-4">
                {paginatedApps.map(app => (
                  <div 
                    key={app.id} 
                    className={`bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition ${
                      app.statut === 'acceptee' ? 'border-l-4 border-l-green-500' :
                      app.statut === 'refusee' ? 'border-l-4 border-l-red-500' :
                      'border-l-4 border-l-yellow-500'
                    }`}
                  >
                    <div className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        {/* Candidate Info */}
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xl font-bold">
                              {(app.nom || app.user_nom || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-bold text-gray-800 text-lg">
                                {app.nom || app.user_nom || 'Candidat'}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                app.statut === 'en_attente' ? 'bg-yellow-100 text-yellow-700' :
                                app.statut === 'acceptee' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {app.statut === 'en_attente' ? 'En attente' : 
                                 app.statut === 'acceptee' ? 'Acceptée' : 'Refusée'}
                              </span>
                            </div>
                            
                            {app.user_email && (
                              <p className="text-gray-500 text-sm mb-2">{app.user_email}</p>
                            )}
                            
                            <div className="flex flex-wrap gap-2 text-sm mb-3">
                              <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full">
                                <Briefcase size={14} className="inline mr-1" />{app.task_titre || 'Poste'}
                              </span>
                              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                                <Folder size={14} className="inline mr-1" />{app.project_titre || 'Projet'}
                              </span>
                            </div>
                            
                            {/* Contact */}
                            {app.contacts && (
                              <div className="bg-blue-50 rounded-xl p-3 mb-3">
                                <p className="text-blue-700 text-sm">
                                  <strong><Phone size={14} className="inline mr-1" />Contact :</strong> {app.contacts.length > 100 ? app.contacts.substring(0, 100) + '...' : app.contacts}
                                </p>
                              </div>
                            )}
                            
                            {/* Motivation */}
                            {app.motivation && (
                              <div className="bg-gray-50 rounded-xl p-3">
                                <p className="text-gray-600 text-sm italic">
                                  <Lightbulb size={14} className="inline mr-1" />"{app.motivation.length > 150 ? app.motivation.substring(0, 150) + '...' : app.motivation}"
                                </p>
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-3">
                              <Calendar size={12} className="inline mr-1" />Reçue le {formatDate(app.date_candidature || app.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2 lg:ml-4">
                          {app.statut === 'en_attente' && (
                            <>
                              <button
                                onClick={() => handleApplicationAction(app.id, 'acceptee')}
                                className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium text-sm flex items-center gap-2 justify-center"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Accepter
                              </button>
                              <button
                                onClick={() => handleApplicationAction(app.id, 'refusee')}
                                className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-medium text-sm flex items-center gap-2 justify-center"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Refuser
                              </button>
                            </>
                          )}
                          <Link
                            to={`/profile/${app.user_id}`}
                            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium text-sm text-center"
                          >
                            <User size={16} className="inline mr-1" />Voir le profil
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText size={40} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {appSearch || appStatus !== 'all' ? 'Aucune candidature trouvée' : 'Aucune candidature reçue'}
                </h3>
                <p className="text-gray-500">
                  {appSearch || appStatus !== 'all'
                    ? 'Essayez de modifier vos filtres'
                    : 'Les candidatures apparaîtront ici quand des personnes postuleront'}
                </p>
              </div>
            )}
            
            <Pagination currentPage={appPage} totalPages={totalAppPages} setPage={setAppPage} />
          </div>
        )}
      </div>
      
      {/* Bottom Padding */}
      <div className="h-12"></div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" 
              onClick={() => setConfirmModal(null)}
            ></div>

            {/* Modal Panel */}
            <div className="relative inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl">
              {/* Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>

              {/* Content */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2" id="modal-title">
                  {confirmModal.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {confirmModal.message}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-center">
                <button
                  type="button"
                  className="w-full sm:w-auto px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setConfirmModal(null)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="w-full sm:w-auto px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 transition shadow-lg shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500"
                  onClick={confirmModal.onConfirm}
                >
                  {confirmModal.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorDashboard;
